import { ItemDefinition, ItemType } from '../types/inventory.types';
import { MODIFIER_REGISTRY } from './modifiers.config';
import { SPELL_REGISTRY } from './spells.config';

// ---------------------------------------------------------------------------
// Item catalogue
// ---------------------------------------------------------------------------

export const ITEMS: Record<string, ItemDefinition> = {
  // ---- Currency ----
  gold: {
    id: 'gold',
    type: ItemType.MATERIAL,
    label: 'Or',
    maxStack: 99999,
    sprite: 'gold',
    sellValue: 1,
    description: 'Monnaie utilisée par les marchands et le forgeron.',
  },

  // ---- Consumables ----
  'pill-life': {
    id: 'pill-life',
    type: ItemType.CONSUMABLE,
    label: 'Pilule de vie',
    maxStack: 1,
    sprite: 'pill-life',
    sellValue: 20,
    effects: [{ handler: 'healHPPercent', params: { percent: 0.5 } }],
    description: 'Restaure 50 % des PV max instantanément.',
  },
  'pill-spirit': {
    id: 'pill-spirit',
    type: ItemType.CONSUMABLE,
    label: "Pilule d'esprit",
    maxStack: 1,
    sprite: 'pill-shield',
    sellValue: 15,
    effects: [{ handler: 'healShield', params: { full: true } }],
    description: 'Restaure entièrement le bouclier.',
  },
  antidote: {
    id: 'antidote',
    type: ItemType.CONSUMABLE,
    label: 'Antidote',
    maxStack: 1,
    sprite: 'antidote',
    sellValue: 12,
    effects: [{ handler: 'cureNegativeStatus', params: {} }],
    description:
      'Soigne tous les états négatifs (poison, saignement, gel, etc.).',
  },
  'elixir-sharp': {
    id: 'elixir-sharp',
    type: ItemType.CONSUMABLE,
    label: 'Élixir tranchant',
    maxStack: 1,
    sprite: 'elixir-sharp',
    sellValue: 80,
    effects: [
      { handler: 'applyBuff', params: { buffId: 'elixir-sharp-buff' } },
    ],
    description: "+30 % de dégâts jusqu'à la fin de l'étage.",
  },
  'elixir-solid': {
    id: 'elixir-solid',
    type: ItemType.CONSUMABLE,
    label: 'Élixir robuste',
    maxStack: 1,
    sprite: 'elixir-solid',
    sellValue: 80,
    effects: [
      { handler: 'applyBuff', params: { buffId: 'elixir-solid-buff' } },
    ],
    description: "+30 % de défenses jusqu'à la fin de l'étage.",
  },
  'elixir-swift': {
    id: 'elixir-swift',
    type: ItemType.CONSUMABLE,
    label: 'Élixir agile',
    maxStack: 1,
    sprite: 'elixir-swift',
    sellValue: 80,
    effects: [
      { handler: 'applyBuff', params: { buffId: 'elixir-swift-buff' } },
    ],
    description: "+30 % de vitesse de déplacement jusqu'à la fin de l'étage.",
  },
  'reviver-seed': {
    id: 'reviver-seed',
    type: ItemType.CONSUMABLE,
    label: 'Graine de réveil',
    maxStack: 1,
    sprite: 'reviver-seed',
    sellValue: 200,
    listeners: [
      {
        event: 'onDeath',
        handler: 'healHPPercent',
        params: { percent: 0.5 },
      },
      {
        event: 'onDeath',
        handler: 'consumeSelf',
        params: {},
      },
    ],
    description:
      'Graine luminescente. Consommée automatiquement à la mort pour ressusciter le porteur avec une partie de ses PV.\nNe compte pas comme une vraie mort.',
  },
  'thunder-in-a-box': {
    id: 'thunder-in-a-box',
    type: ItemType.CONSUMABLE,
    label: 'Tonnerre en boîte',
    maxStack: 1,
    sprite: 'thunder-in-a-box',
    sellValue: 40,
    effects: [
      {
        handler: 'dealFixedDamageInCells',
        params: { amount: 30, radius: 2, type: 'magic', element: 'anger' },
      },
    ],
    description: 'Dégâts de foudre en zone (rayon 2).',
  },
  // ---- Materials (trinket mastery resources) ----
  'soul-shard': {
    id: 'soul-shard',
    type: ItemType.MATERIAL,
    label: "Fragment d'affection",
    maxStack: 99,
    sprite: 'soul-shard',
    sellValue: 10,
    description: "Éclat d'essence. Sert à améliorer les babioles.",
  },
  'spirit-heart': {
    id: 'spirit-heart',
    type: ItemType.MATERIAL,
    label: "Très gros morceau d'affection",
    maxStack: 99,
    sprite: 'spirit-heart',
    sellValue: 50,
    description: "Cœur d'esprit. Sert à améliorer les trésors.",
  },

  // ---- Keys ----
  key: {
    id: 'key',
    type: ItemType.KEY,
    label: 'Clé',
    maxStack: 1,
    sprite: 'key',
    sellValue: 0,
    description: 'Ouvre une porte de chambre forte scellée.',
  },

  // ---- Technical items ----

  'escape-orb': {
    id: 'escape-orb',
    type: ItemType.CONSUMABLE,
    label: "Orbe d'évasion",
    maxStack: 1,
    sprite: 'escape-orb',
    sellValue: 300,
    effects: [{ handler: 'returnToHub', params: {} }],
    description:
      'Orbe lumineuse. Consommée pour quitter immédiatement le donjon avec tout le butin.\nCompte comme une fin de run réussie.',
  },
};

// ---------------------------------------------------------------------------
// Boost items (one per modifier — generated at module load time)
// ---------------------------------------------------------------------------

const BOOST_ITEMS: Record<string, ItemDefinition> = Object.fromEntries(
  Object.values(MODIFIER_REGISTRY).map((mod) => {
    const id = `boost-${mod.id}`;
    return [
      id,
      {
        id,
        type: ItemType.BOOST,
        label: mod.name,
        maxStack: 1,
        sprite: 'run-modifier',
        sellValue: 0,
        modifierId: mod.id,
        description: mod.displayTemplate,
      } satisfies ItemDefinition,
    ];
  }),
);

Object.assign(ITEMS, BOOST_ITEMS);

// ---------------------------------------------------------------------------
// Grimoire items (one per learnable spell — generated at module load time).
// On use: unlockSpell; if already known, grants mastery XP instead.
// ---------------------------------------------------------------------------

const GRIMOIRE_ITEMS: Record<string, ItemDefinition> = Object.fromEntries(
  Object.values(SPELL_REGISTRY)
    .filter((spell) => !spell.excludeFromGrimoire)
    .map((spell) => {
      const id = `grimoire-${spell.id}`;
      return [
        id,
        {
          id,
          type: ItemType.CONSUMABLE,
          label: 'Souvenir : ' + spell.name,
          maxStack: 1,
          sprite: 'grimoire',
          sellValue: 50,
          effects: [{ handler: 'unlockSpell', params: { spellId: spell.id } }],
          description:
            'Apprend ' +
            spell.name +
            ". Si déjà connu, donne de l'XP de maîtrise.",
          iconOverlay: spell.icon,
        } satisfies ItemDefinition,
      ];
    }),
);

Object.assign(ITEMS, GRIMOIRE_ITEMS);

// ---------------------------------------------------------------------------
// Inventory grid config
// ---------------------------------------------------------------------------

/** Number of columns in the inventory grid. */
export const INVENTORY_COLS = 5;

/** Number of rows in the inventory grid. */
export const INVENTORY_ROWS = 4;

/**
 * Capacity (max number of distinct items). Storage is packed — no interleaved
 * empty slots; any `addItem` exceeding capacity returns the overflow.
 */
export const INVENTORY_SIZE = INVENTORY_COLS * INVENTORY_ROWS;

let itemOrder: Map<string, number> | null = null;

/**
 * Sort rank by declaration order in the fully-assembled `ITEMS` catalogue
 * (static items, then generated boosts, then grimoires). Unknown ids sort last.
 * Built lazily on first call so all module-load `Object.assign(ITEMS, …)` have run.
 */
export function itemOrderRank(itemId: string): number {
  if (!itemOrder) {
    itemOrder = new Map(Object.keys(ITEMS).map((id, i) => [id, i]));
  }
  return itemOrder.get(itemId) ?? itemOrder.size;
}
